import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Star, Users, Sparkles, LogOut, Flame, Award, 
  ChevronRight, Calendar, HelpCircle, FolderOpen, Target, 
  Briefcase, CheckCircle, Brain, MapPin, ArrowLeft, Zap, Play
} from 'lucide-react';

// YAS Design V3: "Smooth Vibes"
// Softer, more flowing with rounded elements and gradients
// Playful: Floating elements, glass morphism, smooth animations feel

const YAS_LOGO = '/yas-logo.png';

const YASDesignV3 = () => {
  const navigate = useNavigate();

  const colors = {
    teal: '#1B2D4F',
    tealLight: '#3A9FB0',
    tealSoft: '#E8F4F6',
    orange: '#E86C3D',
    orangeSoft: '#FFF0EB',
    peach: '#FFB59A',
    cream: '#FFFBF7',
    slate: '#3D5A6C',
    mint: '#C7E8E4',
  };

  return (
    <div className="min-h-screen pb-24" style={{ 
      backgroundColor: colors.cream,
      fontFamily: "'Quicksand', sans-serif"
    }}>
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 bg-white/80 backdrop-blur-xl rounded-full p-3 shadow-lg hover:shadow-xl transition-all"
      >
        <ArrowLeft className="w-5 h-5" style={{ color: colors.teal }} />
      </button>

      {/* Theme Label */}
      <div 
        className="fixed top-4 right-4 z-50 bg-white/80 backdrop-blur-xl px-4 py-2 rounded-full font-bold text-sm shadow-lg"
        style={{ color: colors.teal }}
      >
        YAS V3: Smooth Vibes
      </div>

      {/* Hero Header */}
      <div className="relative overflow-hidden">
        {/* Gradient background with mesh effect */}
        <div 
          className="absolute inset-0" 
          style={{ 
            background: `
              radial-gradient(ellipse at 30% 20%, ${colors.teal} 0%, transparent 50%),
              radial-gradient(ellipse at 80% 80%, ${colors.orange}90 0%, transparent 40%),
              radial-gradient(ellipse at 50% 50%, ${colors.tealLight} 0%, ${colors.teal} 100%)
            `
          }} 
        />
        
        {/* Floating glass circles */}
        <div 
          className="absolute top-16 right-8 w-24 h-24 rounded-full bg-white/10 backdrop-blur-md"
          style={{ animation: 'float 6s ease-in-out infinite' }}
        />
        <div 
          className="absolute top-32 left-4 w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm"
          style={{ animation: 'float 8s ease-in-out infinite reverse' }}
        />
        <div 
          className="absolute bottom-20 right-16 w-12 h-12 rounded-full"
          style={{ backgroundColor: colors.peach, opacity: 0.6, animation: 'float 5s ease-in-out infinite' }}
        />
        
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-15px); }
          }
        `}</style>
        
        <div className="relative px-4 pt-16 pb-12">
          <div className="max-w-md mx-auto">
            {/* Logo Bar */}
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 bg-white/90 backdrop-blur-xl rounded-2xl flex items-center justify-center shadow-lg overflow-hidden">
                  <img src={YAS_LOGO} alt="YAS" className="w-11 h-11 object-contain" />
                </div>
                <div className="text-white">
                  <p className="text-xs font-semibold opacity-80">Youth 2 Adult Success</p>
                  <p className="text-sm font-bold">Summer 2026</p>
                </div>
              </div>
              <button className="bg-white/20 backdrop-blur-xl rounded-full p-3 hover:bg-white/30 transition-colors">
                <LogOut className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Welcome Section - Centered with floating avatar */}
            <div className="text-center mb-8">
              <div className="relative inline-block">
                <div 
                  className="w-28 h-28 rounded-full bg-white/90 backdrop-blur-xl flex items-center justify-center shadow-2xl mx-auto mb-4"
                >
                  <span className="text-5xl font-bold" style={{ color: colors.teal, fontFamily: "'Fredoka', sans-serif" }}>B</span>
                </div>
                {/* Status indicator */}
                <div 
                  className="absolute bottom-4 right-0 w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: colors.orange }}
                >
                  <Play className="w-4 h-4 text-white fill-current" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Hey Brandon
              </h1>
              <p className="text-white/70 text-sm font-medium mb-3">Ready to level up today?</p>
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-full px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-white text-sm font-semibold">UpNEXT SYEP</span>
              </div>
            </div>

            {/* Worksite - Glass card */}
            <div 
              className="bg-white/20 backdrop-blur-xl rounded-3xl p-4 border border-white/30"
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-11 h-11 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: colors.peach }}
                >
                  <MapPin className="w-5 h-5" style={{ color: colors.orange }} />
                </div>
                <div className="text-white">
                  <p className="text-xs font-semibold opacity-70">Current Location</p>
                  <p className="font-bold">Newark Public Library</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Curved bottom with blur */}
        <div 
          className="absolute -bottom-1 left-0 right-0 h-12"
          style={{ 
            background: colors.cream,
            borderRadius: '50% 50% 0 0 / 100% 100% 0 0'
          }}
        />
      </div>

      {/* Stats Cards - Floating above */}
      <div className="max-w-md mx-auto px-4 -mt-8 relative z-10">
        <div className="grid grid-cols-2 gap-4">
          {/* Points Card */}
          <div 
            className="bg-white rounded-3xl p-5 shadow-xl"
            style={{ boxShadow: `0 10px 40px ${colors.teal}20` }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: colors.orangeSoft }}
              >
                <Star className="w-5 h-5" style={{ color: colors.orange }} fill="currentColor" />
              </div>
            </div>
            <p className="text-xs font-bold mb-1" style={{ color: colors.slate }}>Total Points</p>
            <p className="text-3xl font-bold" style={{ color: colors.teal, fontFamily: "'Fredoka', sans-serif" }}>
              1,250
            </p>
          </div>

          {/* Streak Card */}
          <div 
            className="bg-white rounded-3xl p-5 shadow-xl"
            style={{ boxShadow: `0 10px 40px ${colors.orange}15` }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div 
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: colors.tealSoft }}
              >
                <Flame className="w-5 h-5" style={{ color: colors.teal }} />
              </div>
            </div>
            <p className="text-xs font-bold mb-1" style={{ color: colors.slate }}>Current Streak</p>
            <p className="text-3xl font-bold" style={{ color: colors.orange, fontFamily: "'Fredoka', sans-serif" }}>
              7 <span className="text-lg" style={{ color: colors.peach }}>days</span>
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        
        {/* Quick Actions - Pill buttons */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
          {[
            { icon: Target, label: 'Goals' },
            { icon: Briefcase, label: 'Project' },
            { icon: Trophy, label: 'Rankings' },
            { icon: Award, label: 'Wins' },
            { icon: FolderOpen, label: 'Files' },
            { icon: HelpCircle, label: 'Help' },
          ].map((item, idx) => (
            <button
              key={item.label}
              className="flex-shrink-0 flex items-center gap-2 px-5 py-3 rounded-full shadow-md hover:shadow-lg transition-all"
              style={{ 
                backgroundColor: idx % 2 === 0 ? colors.tealSoft : colors.orangeSoft,
                color: idx % 2 === 0 ? colors.teal : colors.orange
              }}
            >
              <item.icon className="w-4 h-4" />
              <span className="text-sm font-bold">{item.label}</span>
            </button>
          ))}
        </div>

        {/* Work Journal Card */}
        <div 
          className="rounded-3xl p-6 relative overflow-hidden"
          style={{ 
            background: `linear-gradient(135deg, ${colors.orange} 0%, ${colors.peach} 100%)`,
            boxShadow: `0 15px 50px ${colors.orange}30`
          }}
        >
          {/* Glass overlay shape */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full blur-lg" />
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white/90 backdrop-blur rounded-2xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6" style={{ color: colors.orange }} />
              </div>
              <div className="text-white">
                <h2 className="text-xl font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  Work Journal
                </h2>
                <p className="text-white/80 text-sm font-medium">Share your weekly wins</p>
              </div>
            </div>
            <button 
              className="w-full bg-white rounded-2xl py-4 font-bold text-lg hover:shadow-lg transition-all"
              style={{ color: colors.orange, fontFamily: "'Fredoka', sans-serif" }}
            >
              Drop a Win! +50 pts
            </button>
          </div>
        </div>

        {/* This Week Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div 
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: colors.tealSoft }}
            >
              <Calendar className="w-4 h-4" style={{ color: colors.teal }} />
            </div>
            <h2 className="text-lg font-bold" style={{ color: colors.slate, fontFamily: "'Fredoka', sans-serif" }}>
              This Week
            </h2>
          </div>

          <div className="space-y-3">
            {/* Assessment Card */}
            <div 
              className="rounded-2xl p-5 cursor-pointer shadow-lg hover:shadow-xl transition-all"
              style={{ 
                background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.tealLight} 100%)`,
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>Skills Assessment</h3>
                    <p className="text-white/80 text-sm">Track your growth • 50 pts</p>
                  </div>
                </div>
                <div 
                  className="px-3 py-1.5 rounded-full text-xs font-bold"
                  style={{ backgroundColor: colors.peach, color: colors.orange }}
                >
                  New
                </div>
              </div>
            </div>

            {/* Check-in Card */}
            <div 
              className="bg-white rounded-2xl p-4 cursor-pointer shadow-md hover:shadow-lg transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: colors.mint }}
                  >
                    <CheckCircle className="w-5 h-5" style={{ color: colors.teal }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.slate, fontFamily: "'Fredoka', sans-serif" }}>Weekly Check-in</h3>
                    <p className="text-sm" style={{ color: colors.tealLight }}>How's your week going? • 20 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5" style={{ color: colors.tealLight }} />
              </div>
            </div>

            {/* Trivia Card */}
            <div 
              className="bg-white rounded-2xl p-4 cursor-pointer shadow-md hover:shadow-lg transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: colors.orangeSoft }}
                  >
                    <Zap className="w-5 h-5" style={{ color: colors.orange }} />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: colors.slate, fontFamily: "'Fredoka', sans-serif" }}>Weekly Trivia</h3>
                    <p className="text-sm" style={{ color: colors.tealLight }}>Test your knowledge • 25 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5" style={{ color: colors.tealLight }} />
              </div>
            </div>
          </div>
        </div>

        {/* Team Card */}
        <div 
          className="bg-white rounded-3xl p-5 relative overflow-hidden shadow-xl"
          style={{ boxShadow: `0 10px 40px ${colors.teal}15` }}
        >
          <div 
            className="absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl"
            style={{ backgroundColor: colors.mint, opacity: 0.5 }}
          />
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 rounded-2xl flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.tealLight} 100%)` }}
                >
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold" style={{ color: colors.slate, fontFamily: "'Fredoka', sans-serif" }}>Team Alpha</h3>
                  <p className="text-sm" style={{ color: colors.tealLight }}>8 awesome members</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold" style={{ color: colors.teal, fontFamily: "'Fredoka', sans-serif" }}>4,500</p>
                <p className="text-xs font-semibold" style={{ color: colors.tealLight }}>team pts</p>
              </div>
            </div>
            <button 
              className="w-full rounded-2xl py-3 font-bold transition-all hover:shadow-md"
              style={{ 
                background: `linear-gradient(135deg, ${colors.tealSoft} 0%, ${colors.mint} 100%)`,
                color: colors.teal,
                fontFamily: "'Fredoka', sans-serif"
              }}
            >
              View Your Squad <ChevronRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div 
        className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-t px-4 py-3"
        style={{ borderColor: colors.tealSoft }}
      >
        <div className="max-w-md mx-auto flex justify-around items-center">
          {[
            { icon: Target, label: 'Home', active: true },
            { icon: Target, label: 'Goals', active: false },
            { icon: Trophy, label: 'Progress', active: false },
            { icon: Users, label: 'Profile', active: false },
          ].map((item) => (
            <button 
              key={item.label} 
              className="flex flex-col items-center gap-1 px-4 py-2 rounded-2xl transition-all"
              style={{ 
                backgroundColor: item.active ? colors.tealSoft : 'transparent',
                color: item.active ? colors.teal : colors.slate
              }}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs font-bold">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default YASDesignV3;
